'use client'

// The art wall — artWall.glb, placements baked (drops in at identity).
// Three nodes: Wall (textured wood), Path (cosmetic concrete), Posters.
//
// The Posters mesh is 8 placeholder quads merged into one plane. We keep
// it hidden and use its decoded geometry as a LAYOUT: triangles cluster
// into quads by x-gap, and each quad's world bounds becomes a hanging
// spot for one of his drawings, pulled from the same public API the
// gallery reads. While the studio is shut the API serves an empty wall,
// so in prod this renders nothing until the arc flips — the bare wall
// IS the pre-arc state, no extra gating here.
//
// Both GLB materials export at roughness 0.5 (semi-gloss), which is why
// the wall was catching the light. Painted wood and concrete are matte:
// forced to 0.95 in the conversion.

import { useEffect, useMemo, useState } from 'react'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { ditherMaterial } from '../effects/ditherRegistry'

const MODEL = '/models/artWall.glb'
const _v = new THREE.Vector3()

// group the posters mesh's triangles into quads by CONNECTIVITY: the
// quads never share vertices with each other, so union-find over the
// index recovers them exactly (an x-gap heuristic merged neighbours)
function quadSlots(posters) {
  posters.updateWorldMatrix(true, false)
  const geom = posters.geometry
  const pos = geom.attributes.position
  const index = geom.index
  const triCount = (index ? index.count : pos.count) / 3

  const parent = Array.from({ length: pos.count }, (_, i) => i)
  const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i] } return i }
  const union = (a, b) => { parent[find(a)] = find(b) }
  for (let t = 0; t < triCount; t++) {
    const a = index ? index.getX(t * 3) : t * 3
    const b = index ? index.getX(t * 3 + 1) : t * 3 + 1
    const c = index ? index.getX(t * 3 + 2) : t * 3 + 2
    union(a, b); union(b, c)
  }

  const groups = new Map()
  for (let vi = 0; vi < pos.count; vi++) {
    const root = find(vi)
    if (!groups.has(root)) groups.set(root, [])
    _v.fromBufferAttribute(pos, vi).applyMatrix4(posters.matrixWorld)
    groups.get(root).push(_v.clone())
  }

  return [...groups.values()]
    .map((pts) => {
      const box = new THREE.Box3().setFromPoints(pts)
      const center = box.getCenter(new THREE.Vector3())
      const size = box.getSize(new THREE.Vector3())
      return { center: [center.x, center.y, center.z], w: size.x, h: size.y }
    })
    .sort((a, b) => a.center[0] - b.center[0])
}

// A private manager, never the default one. The default manager is wired
// straight into the loading veil (drei's useProgress turns every itemStart
// into a react setState), so 26 drawings mounting while the veil was still
// up meant 26 synchronous store writes in one commit cascade — the
// during-load #185 the crash reports kept circling. And one hung
// /api/drawings response on the default manager parked the veil's counter
// short of 100 forever, which was the stuck-at-99 loader. The wall is
// ambient content; it hangs whenever it arrives and answers to nobody.
//
// And an ImageBitmap loader rather than a TextureLoader. Measured on the
// live site 14 Sep: the eight drawings on the current wall finished
// downloading at 1.2s (376KB of PNG each) and the main thread then froze
// for 1.15s, the one long task in the first ten seconds: eight 512x512
// images decoded and uploaded in the same frame, in the busiest second
// of the boot. createImageBitmap decodes off the main thread, and the
// bitmap is flipped by the browser (imageOrientation) so flipY is off on
// the texture, which is the recipe that holds on both the WebGPU and the
// WebGL fallback path.
const wallLoader = new THREE.ImageBitmapLoader(new THREE.LoadingManager())
wallLoader.setOptions({ imageOrientation: 'flipY', premultiplyAlpha: 'none', colorSpaceConversion: 'none' })
// First hang a few seconds after the wall mounts (the world is up by
// then), and one at a time after that: the same work spread over three
// seconds instead of one frame.
const FIRST_HANG_MS = 3500
const HANG_EVERY_MS = 300

function Poster({ slot, id, order = 0 }) {
  const [tex, setTex] = useState(null)

  // Built and dithered in the same breath. The declarative material used
  // to mount whenever the texture landed, then wait up to three seconds
  // for the dither sweep to find it: one clean flash per drawing, the
  // exact flicker the billboards had. His cats hang looking like the
  // world from their first frame.
  const mat = useMemo(() => {
    const m = new THREE.MeshStandardNodeMaterial()
    m.roughness = 0.95
    m.metalness = 0
    m.side = THREE.DoubleSide
    ditherMaterial(m)
    return m
  }, [])
  useEffect(() => () => { mat.dispose() }, [mat])

  useEffect(() => {
    let dead = false
    const timer = setTimeout(() => {
      wallLoader.load(`/api/drawings/${id}`, (bitmap) => {
        if (dead) { try { bitmap.close?.() } catch { /* gone */ } return }
        const t = new THREE.Texture(bitmap)
        t.flipY = false
        t.colorSpace = THREE.SRGBColorSpace
        // The look the pass enforces, set before the first draw.
        t.magFilter = THREE.NearestFilter
        t.minFilter = THREE.NearestFilter
        t.generateMipmaps = false
        t.needsUpdate = true
        mat.map = t
        mat.needsUpdate = true
        setTex(t)
      })
    }, FIRST_HANG_MS + order * HANG_EVERY_MS)
    return () => { dead = true; clearTimeout(timer) }
  }, [id, mat, order])

  if (!tex) return null
  return (
    // faces the path side; drawings hang with a small margin inside the
    // placeholder quad and sit a hair off the wall to avoid z-fighting
    <mesh position={[slot.center[0], slot.center[1], slot.center[2] - 0.03]} rotation={[0, Math.PI, 0]}>
      <planeGeometry args={[Math.min(slot.w, slot.h) * 0.94, Math.min(slot.w, slot.h) * 0.94]} />
      <primitive object={mat} attach="material" />
    </mesh>
  )
}

export function ArtWall() {
  const { scene } = useModel(MODEL)
  const [slots, setSlots] = useState([])
  const [drawings, setDrawings] = useState([])

  useEffect(() => {
    const converted = new Map()
    scene.traverse((o) => {
      // Poster placeholders stay hidden: their geometry is the layout
      // the real drawings hang on (see quadSlots below).
      if (o.name === 'Posters') {
        o.visible = false
        try { setSlots(quadSlots(o)) } catch { /* bare wall then */ }
        return
      }
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      const src = o.material
      if (!src || Array.isArray(src) || src.isNodeMaterial) return
      let nm = converted.get(src)
      if (!nm) {
        nm = new THREE.MeshStandardNodeMaterial()
        if (src.map) nm.map = src.map
        nm.color = src.color?.clone() ?? new THREE.Color('white')
        nm.roughness = 0.95
        nm.metalness = 0
        nm.side = src.side
        nm.vertexColors = src.vertexColors
        converted.set(src, nm)
      }
      o.material = nm
    })
  }, [scene])

  useEffect(() => {
    let dead = false
    fetch('/api/drawings?limit=8')
      .then((r) => r.json())
      .then((d) => { if (!dead && Array.isArray(d.drawings)) setDrawings(d.drawings) })
      .catch(() => { /* bare wall */ })
    return () => { dead = true }
  }, [])

  // newest 8, hung oldest to newest left to right
  const hung = drawings.slice(0, slots.length).reverse()

  return (
    <>
      <primitive object={scene} />
      {hung.map((d, i) => slots[i] && <Poster key={d.id} slot={slots[i]} id={d.id} order={i} />)}
    </>
  )
}

export default ArtWall
